"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type RefObject } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

/** How long the mark waits for its target before showing at the top of the page instead. */
export const COACH_TARGET_TIMEOUT_MS = 5000;
const selectorFor = (targetId: string) => `[data-spotlight-target="${CSS.escape(targetId)}"]`;

/**
 * The one-time mark after the popup (spec cova-feed/2026-10-09-001 §6.1): the
 * broker acted, landed on the exact screen, and ONE bubble points at the new
 * thing. "Got it" retires it for good.
 *
 * Anchored with a Radix Popover `virtualRef` to the page's
 * `[data-spotlight-target]`, so a destination page carries an attribute and
 * nothing else. If the target has not appeared within 5 s (a slow page, a
 * moved element), the bubble shows top-centre rather than nothing: the
 * broker still learns what is new, and the miss is reported.
 */
export function SpotlightCoachMark(t0) {
  const $ = _c(18);
  const {
    targetId,
    text,
    onDismiss,
    onShown,
    onTargetMissing
  } = t0;
  const anchorRef = useRef(null);
  const [placement, setPlacement] = useState("waiting");
  let t1;
  let t2;
  if ($[0] !== targetId) {
    t1 = () => {
      const find = () => {
        const target = document.querySelector(selectorFor(targetId));
        if (target === null) {
          return null;
        }
        const child = target.firstElementChild;
        return target.getClientRects().length === 0 && child instanceof HTMLElement ? child : target;
      };
      const settle = target_0 => {
        anchorRef.current = target_0;
        observer.disconnect();
        window.clearTimeout(timer);
        setPlacement("anchored");
      };
      const observer = new MutationObserver(() => {
        const target_1 = find();
        if (target_1) {
          settle(target_1);
        }
      });
      const timer = window.setTimeout(() => {
        observer.disconnect();
        setPlacement("fallback");
      }, COACH_TARGET_TIMEOUT_MS);
      const frame = window.requestAnimationFrame(() => {
        const target_2 = find();
        if (target_2) {
          settle(target_2);
        } else {
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        }
      });
      return () => {
        window.cancelAnimationFrame(frame);
        observer.disconnect();
        window.clearTimeout(timer);
      };
    };
    t2 = [targetId];
    $[0] = targetId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const shownRef = useRef(false);
  let t3;
  let t4;
  if ($[3] !== onShown || $[4] !== onTargetMissing || $[5] !== placement) {
    t3 = () => {
      if (placement === "waiting" || shownRef.current) {
        return;
      }
      shownRef.current = true;
      if (placement === "fallback") {
        onTargetMissing?.();
      }
      onShown?.(placement === "anchored");
    };
    t4 = [placement, onShown, onTargetMissing];
    $[3] = onShown;
    $[4] = onTargetMissing;
    $[5] = placement;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  if (placement === "waiting") {
    return null;
  }
  let t5;
  if ($[8] !== onDismiss || $[9] !== text) {
    t5 = <CoachBubble text={text} onDismiss={onDismiss} />;
    $[8] = onDismiss;
    $[9] = text;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const bubble = t5;
  if (placement === "fallback") {
    let t6;
    if ($[11] !== bubble) {
      t6 = <div className="pointer-events-none fixed inset-x-0 top-20 z-50 flex justify-center px-4"><div className="pointer-events-auto">{bubble}</div></div>;
      $[11] = bubble;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PopoverPrimitive.Anchor virtualRef={anchorRef as RefObject<HTMLElement>} />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <PopoverPrimitive.Arrow className="fill-[#091E42]" width={14} height={7} />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== bubble || $[16] !== onDismiss) {
    t8 = <PopoverPrimitive.Root open={true}>{t6}<PopoverPrimitive.Portal><PopoverPrimitive.Content side="bottom" align="center" sideOffset={12} collisionPadding={16} onOpenAutoFocus={_temp} onInteractOutside={_temp2} onEscapeKeyDown={onDismiss} className="z-50 outline-hidden">{bubble}{t7}</PopoverPrimitive.Content></PopoverPrimitive.Portal></PopoverPrimitive.Root>;
    $[15] = bubble;
    $[16] = onDismiss;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp2(event_0) {
  return event_0.preventDefault();
}
function _temp(event) {
  return event.preventDefault();
}
function CoachBubble(t0) {
  const $ = _c(11);
  const {
    text,
    onDismiss,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("w-64 rounded-2xl bg-[#091E42] px-3.5 py-3 text-left text-white shadow-[0_18px_40px_-12px_rgba(9,30,66,0.6)]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-[#5651ff] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"><Sparkles className="h-3 w-3" aria-hidden={true} />New</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== text) {
    t3 = <span className="block text-[13px] leading-snug">{text}</span>;
    $[3] = text;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onDismiss) {
    t4 = <button type="button" onClick={onDismiss} className="mt-2.5 inline-flex h-7 items-center rounded-full bg-white px-3 text-xs font-semibold text-[#091E42] transition-all hover:bg-white/90 active:scale-[0.97]">Got it</button>;
    $[5] = onDismiss;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div role="status" className={t1}>{t2}{t3}{t4}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}